import api from '@/lib/api';
import type { Contact, Communication, Deal, Appointment } from '@/types/models';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || !import.meta.env.VITE_API_URL;

// ==================== Mock Data ====================

const MOCK_CONTACTS: Contact[] = [
  {
    id: 'ct-1', customer_id: 'cust-1', customer_name: 'Acme Corp',
    first_name: 'John', last_name: 'Smith', email: 'john@acme.com', phone: '555-0101',
    title: 'IT Director', is_primary: true, tags: ['enterprise', 'recurring'],
    notes: 'Key decision maker for all ITAD contracts',
    budget_confirmed: true, budget_range: '50k_100k',
    is_decision_maker: true, authority_level: 'decision_maker',
    need_identified: true, need_urgency: 'high',
    timeline: '3_6mo', lead_score: 88,
    created_at: '2025-01-15T10:00:00Z', updated_at: '2025-03-20T14:00:00Z',
  },
  {
    id: 'ct-2', customer_id: 'cust-1', customer_name: 'Acme Corp',
    first_name: 'Sarah', last_name: 'Johnson', email: 'sarah@acme.com', phone: '555-0102',
    title: 'Procurement Manager', is_primary: false, tags: ['procurement'],
    budget_confirmed: false, budget_range: 'unknown',
    is_decision_maker: false, authority_level: 'evaluator',
    need_identified: true, need_urgency: 'medium',
    timeline: '6_12mo', lead_score: 44,
    created_at: '2025-02-10T10:00:00Z', updated_at: '2025-03-15T10:00:00Z',
  },
  {
    id: 'ct-3', customer_id: 'cust-2', customer_name: 'TechStart Inc',
    first_name: 'Mike', last_name: 'Chen', email: 'mike@techstart.io', phone: '555-0201',
    title: 'CTO', is_primary: true, tags: ['startup'],
    budget_confirmed: true, budget_range: '10k_50k',
    is_decision_maker: true, authority_level: 'champion',
    need_identified: true, need_urgency: 'critical',
    timeline: 'under_3mo', lead_score: 95,
    created_at: '2025-03-01T10:00:00Z', updated_at: '2025-03-25T10:00:00Z',
  },
];

const MOCK_COMMUNICATIONS: Communication[] = [
  {
    id: 'comm-1', contact_id: 'ct-1', customer_id: 'cust-1',
    channel: 'phone', direction: 'outbound',
    subject: 'Quarterly review call', body: 'Discussed upcoming refresh of 200 laptops. Customer interested in certified destruction.',
    duration_seconds: 1200, outcome: 'positive',
    logged_by: 'dev-1', logged_by_name: 'Dev User',
    occurred_at: '2025-03-20T14:00:00Z', created_at: '2025-03-20T14:20:00Z',
  },
  {
    id: 'comm-2', contact_id: 'ct-1', customer_id: 'cust-1',
    channel: 'email', direction: 'outbound',
    subject: 'Proposal: Q2 ITAD Services', body: 'Sent pricing proposal for 200-unit laptop refresh including certified data destruction.',
    logged_by: 'dev-1', logged_by_name: 'Dev User',
    occurred_at: '2025-03-21T09:00:00Z', created_at: '2025-03-21T09:05:00Z',
  },
  {
    id: 'comm-3', contact_id: 'ct-3', customer_id: 'cust-2',
    channel: 'sms', direction: 'inbound',
    body: 'Hey, when can you pick up the servers? We need them gone by Friday.',
    logged_by: 'dev-1', logged_by_name: 'Dev User',
    occurred_at: '2025-03-24T11:30:00Z', created_at: '2025-03-24T11:30:00Z',
  },
];

const MOCK_DEALS: Deal[] = [
  {
    id: 'deal-1', customer_id: 'cust-1', customer_name: 'Acme Corp',
    contact_id: 'ct-1', contact_name: 'John Smith',
    title: 'Q2 Laptop Refresh — 200 units', value: 75000,
    stage: 'proposal', expected_close_date: '2025-06-15',
    assigned_to: 'dev-1', assigned_to_name: 'Dev User',
    notes: 'Includes certified destruction. Customer comparing with competitor.',
    budget_confirmed: true, budget_range: '50k_100k',
    is_decision_maker: true, authority_level: 'decision_maker',
    need_identified: true, need_urgency: 'high',
    timeline: '3_6mo', lead_score: 88,
    created_at: '2025-03-15T10:00:00Z', updated_at: '2025-03-22T10:00:00Z',
  },
  {
    id: 'deal-2', customer_id: 'cust-2', customer_name: 'TechStart Inc',
    contact_id: 'ct-3', contact_name: 'Mike Chen',
    title: 'Server decommission — 50 units', value: 22000,
    stage: 'qualified', expected_close_date: '2025-04-30',
    assigned_to: 'dev-1', assigned_to_name: 'Dev User',
    budget_confirmed: true, budget_range: '10k_50k',
    is_decision_maker: true, authority_level: 'champion',
    need_identified: true, need_urgency: 'critical',
    timeline: 'under_3mo', lead_score: 95,
    created_at: '2025-03-10T10:00:00Z', updated_at: '2025-03-24T10:00:00Z',
  },
  {
    id: 'deal-3', customer_id: 'cust-1', customer_name: 'Acme Corp',
    contact_id: 'ct-2', contact_name: 'Sarah Johnson',
    title: 'Office equipment disposal', value: 8000,
    stage: 'lead',
    budget_confirmed: false, budget_range: 'unknown',
    is_decision_maker: false, authority_level: 'evaluator',
    need_identified: false, need_urgency: 'low',
    timeline: 'over_12mo', lead_score: 18,
    created_at: '2025-03-22T10:00:00Z', updated_at: '2025-03-22T10:00:00Z',
  },
];

const MOCK_APPOINTMENTS: Appointment[] = [
  {
    id: 'apt-1', contact_id: 'ct-1', contact_name: 'John Smith',
    customer_id: 'cust-1', customer_name: 'Acme Corp', deal_id: 'deal-1',
    type: 'meeting', title: 'Contract review meeting',
    start_at: '2025-04-02T10:00:00Z', end_at: '2025-04-02T11:00:00Z',
    location: 'Acme Corp HQ — Conference Room B',
    notes: 'Bring printed proposal and destruction cert samples',
    status: 'scheduled',
    created_at: '2025-03-25T10:00:00Z', updated_at: '2025-03-25T10:00:00Z',
  },
  {
    id: 'apt-2', contact_id: 'ct-3', contact_name: 'Mike Chen',
    customer_id: 'cust-2', customer_name: 'TechStart Inc',
    type: 'site_visit', title: 'Server room walkthrough',
    start_at: '2025-04-01T14:00:00Z', end_at: '2025-04-01T15:30:00Z',
    location: '123 Tech Blvd, Suite 400',
    status: 'scheduled',
    created_at: '2025-03-24T10:00:00Z', updated_at: '2025-03-24T10:00:00Z',
  },
];

// ==================== Service ====================

function unwrapCollection<T>(payload: unknown): T[] {
  if (!payload) return [];
  const data = (payload as Record<string, unknown>).data;
  if (Array.isArray(data)) return data as T[];
  if (Array.isArray(payload)) return payload as T[];
  return [];
}

function unwrapResource<T>(payload: unknown): T {
  const data = (payload as Record<string, unknown>)?.data;
  return (data ?? payload) as T;
}

export const crmService = {
  // Contacts
  async getContacts(): Promise<Contact[]> {
    if (USE_MOCK) return MOCK_CONTACTS;
    try {
      const res = await api.get('/api/contacts');
      return unwrapCollection<Contact>(res.data);
    } catch {
      return MOCK_CONTACTS;
    }
  },
  async getContact(id: string): Promise<Contact> {
    if (USE_MOCK) return MOCK_CONTACTS.find(c => c.id === id)!;
    const res = await api.get(`/api/contacts/${id}`);
    return unwrapResource<Contact>(res.data);
  },
  async createContact(data: Partial<Contact>): Promise<Contact> {
    if (USE_MOCK) return { ...MOCK_CONTACTS[0], ...data, id: `ct-${Date.now()}` } as Contact;
    try {
      const res = await api.post('/api/contacts', data);
      return unwrapResource<Contact>(res.data);
    } catch {
      return { ...MOCK_CONTACTS[0], ...data, id: `ct-${Date.now()}` } as Contact;
    }
  },
  async updateContact(id: string, data: Partial<Contact>): Promise<Contact> {
    if (USE_MOCK) return { ...MOCK_CONTACTS.find(c => c.id === id)!, ...data };
    const res = await api.patch(`/api/contacts/${id}`, data);
    return unwrapResource<Contact>(res.data);
  },

  // Communications
  async getCommunications(contactId: string): Promise<Communication[]> {
    if (USE_MOCK) return MOCK_COMMUNICATIONS.filter(c => c.contact_id === contactId);
    const res = await api.get(`/api/contacts/${contactId}/communications`);
    return unwrapCollection<Communication>(res.data);
  },
  async logCommunication(contactId: string, data: Partial<Communication>): Promise<Communication> {
    if (USE_MOCK) return { ...MOCK_COMMUNICATIONS[0], ...data, id: `comm-${Date.now()}`, contact_id: contactId } as Communication;
    const res = await api.post(`/api/contacts/${contactId}/communications`, data);
    return unwrapResource<Communication>(res.data);
  },

  // Deals
  async getDeals(): Promise<Deal[]> {
    if (USE_MOCK) return MOCK_DEALS;
    try {
      const res = await api.get('/api/deals');
      return unwrapCollection<Deal>(res.data);
    } catch {
      return [];
    }
  },
  async getDeal(id: string): Promise<Deal> {
    if (USE_MOCK) return MOCK_DEALS.find(d => d.id === id)!;
    const res = await api.get(`/api/deals/${id}`);
    return unwrapResource<Deal>(res.data);
  },
  async createDeal(data: Partial<Deal>): Promise<Deal> {
    if (USE_MOCK) return { ...MOCK_DEALS[0], ...data, id: `deal-${Date.now()}` } as Deal;
    const res = await api.post('/api/deals', data);
    return unwrapResource<Deal>(res.data);
  },
  async updateDeal(id: string, data: Partial<Deal>): Promise<Deal> {
    if (USE_MOCK) return { ...MOCK_DEALS.find(d => d.id === id)!, ...data };
    const res = await api.patch(`/api/deals/${id}`, data);
    return unwrapResource<Deal>(res.data);
  },

  // Appointments
  async getAppointments(): Promise<Appointment[]> {
    if (USE_MOCK) return MOCK_APPOINTMENTS;
    try {
      const res = await api.get('/api/appointments');
      return unwrapCollection<Appointment>(res.data);
    } catch {
      return [];
    }
  },
  async createAppointment(data: Partial<Appointment>): Promise<Appointment> {
    if (USE_MOCK) return { ...MOCK_APPOINTMENTS[0], ...data, id: `apt-${Date.now()}` } as Appointment;
    const res = await api.post('/api/appointments', data);
    return unwrapResource<Appointment>(res.data);
  },
  async updateAppointment(id: string, data: Partial<Appointment>): Promise<Appointment> {
    if (USE_MOCK) return { ...MOCK_APPOINTMENTS.find(a => a.id === id)!, ...data };
    const res = await api.patch(`/api/appointments/${id}`, data);
    return unwrapResource<Appointment>(res.data);
  },

  // Dashboard
  async getDashboard() {
    if (USE_MOCK) {
      return {
        total_contacts: MOCK_CONTACTS.length,
        open_deals: MOCK_DEALS.filter(d => !['won', 'lost'].includes(d.stage)).length,
        pipeline_value: MOCK_DEALS.filter(d => !['won', 'lost'].includes(d.stage)).reduce((s, d) => s + d.value, 0),
        upcoming_appointments: MOCK_APPOINTMENTS.filter(a => a.status === 'scheduled').length,
        recent_communications: MOCK_COMMUNICATIONS.slice(0, 5),
        deals_by_stage: {
          lead: MOCK_DEALS.filter(d => d.stage === 'lead').length,
          qualified: MOCK_DEALS.filter(d => d.stage === 'qualified').length,
          proposal: MOCK_DEALS.filter(d => d.stage === 'proposal').length,
          negotiation: MOCK_DEALS.filter(d => d.stage === 'negotiation').length,
          won: MOCK_DEALS.filter(d => d.stage === 'won').length,
          lost: MOCK_DEALS.filter(d => d.stage === 'lost').length,
        },
      };
    }
    const res = await api.get('/api/crm/dashboard');
    return res.data;
  },
};
